// Modal-Schicht — EINE Quelle für den Schleier unter jedem Overlay, per Portal an `document.body`.
//
// Herkunft: bis hierher stand dieser Baustein als lokale Konstante in
// `pages/TopicCategoryManagement.tsx` und diente allein deren beiden Formularen. Er ist hier
// unverändert übernommen; einzige Ergänzung ist die Eigenschaft `stapelKlasse`, deren Vorgabewert
// exakt die bisherige Klassenkette ergibt (Begründung an der Eigenschaft).
//
// ⚠️ Das Portal ist der eigentliche Punkt, nicht die Bequemlichkeit einer geteilten Klassenkette.
// Die Formulare stehen als Kinder von `PageLayout` im Baum, und dessen Rahmen trägt `space-y-6`.
// Tailwind setzt diesen Rhythmus über `> :not([hidden]) ~ :not([hidden])` — ein Geschwister-
// Selektor, der nicht danach fragt, ob das Kind überhaupt im Fluss steht. Er traf deshalb auch das
// `position: fixed`-Overlay: gemessen `margin-top: 24px`, das `inset-0` begann bei y=24 statt y=0,
// und über dem Modal blieb ein undimmter Streifen quer über die Seite stehen.
//
// `!mt-0` am Overlay wäre die Symptombehandlung gewesen — an jedem Modal einzeln nachzutragen und
// beim nächsten wieder vergessen. Ein Overlay, das die ganze Seite deckt, gehört strukturell nicht
// in den senkrechten Rhythmus einer Seite: `createPortal` nimmt es aus dem `space-y-6`-Rahmen
// heraus, damit greift der Geschwister-Selektor nicht mehr, und die Abstände der übrigen Kacheln
// bleiben unberührt.
//
// Die Stapelordnung ändert sich dadurch NICHT: zwischen Dokumentwurzel und Seiteninhalt liegt kein
// Stapelkontext (`Layout.tsx` setzt weder `transform` noch `opacity` noch `z-index` auf dem Pfad),
// `document.body` ist also derselbe Kontext wie vorher. Der schwebende Balken oben bleibt mit
// `z-[60]` über dem `z-50` der Vorgabe — am gebauten Stylesheet mit einer Trefferprobe in der
// Balkenmitte nachgemessen, nicht angenommen.
import React from 'react';
import { createPortal } from 'react-dom';

/**
 * Vorgabe-Stapelhöhe. Genau der Wert, den alle Overlays dieser Anwendung tragen: die beiden
 * Themen-Formulare, `.modal-overlay` und `.bunny-file-picker-overlay` in `index.css`, der
 * Titel-Wähler in `playlists/TrackPicker.tsx`. Am Stylesheet nachgezählt, nicht geschätzt.
 *
 * ⚠️ Bewusst NICHT exportiert: `eslint-plugin-react-refresh` erlaubt einer Datei mit
 * Komponenten-Export keine zusätzlichen Wert-Exporte (dieselbe Begründung wie beim Rückruf
 * `seitenwechsel`, der deshalb in `filterZustand.ts` statt in `SeitenblaetterFuss.tsx` steht).
 */
const MODAL_STAPEL = 'z-50';

interface ModalSchichtProps {
  children: React.ReactNode;
  /**
   * Stapelhöhe des Schleiers. Ohne Angabe `z-50` — damit rendert dieser Baustein für die
   * Themen-Formulare Zeichen für Zeichen dieselbe Klassenkette wie vor dem Herausheben.
   *
   * Die Eigenschaft existiert für den einen Fall, in dem `z-50` strukturell falsch ist: eine
   * Rückfrage, die AUS einem offenen Formular heraus gestellt wird (Löschen-Knopf im Modal). Bei
   * Gleichstand entschiede die Dokumentreihenfolge — und beide Schichten hängen am selben
   * `document.body`, die zuletzt montierte gewönne. Das wäre eine Zufallsentscheidung darüber, ob
   * die Nutzerin ihre Sicherheitsabfrage sieht. Der Aufrufer legt die Ordnung deshalb ausdrücklich
   * fest; die Begründung des gewählten Werts steht bei ihm, siehe `Rueckfrage.tsx`.
   */
  stapelKlasse?: string;
}

export const ModalSchicht: React.FC<ModalSchichtProps> = ({
  children,
  stapelKlasse = MODAL_STAPEL,
}) =>
  createPortal(
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 ${stapelKlasse} flex items-center justify-center p-4`}
    >
      {children}
    </div>,
    document.body,
  );
